"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Bars3Icon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ShoppingCartIcon,
} from "@heroicons/react/20/solid";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { RootState } from "../../app/rootReducer";
import { openSearchModal } from "@/modules/hub/features/Search/slices/searchSlice";
import clsx from "clsx";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useSidebar } from "@/components/ui/sidebar";
import ThemeToggle from "@/components/ThemeToggle";
import { stopCrossAppImpersonation } from "@/api/impersonation/impersonationApi";

interface TopNavBarProps {
  onSidebarToggle: () => void;
}

const TopNavBar: React.FC<TopNavBarProps> = ({ onSidebarToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { isMobile, toggleSidebar } = useSidebar();

  // Cart state from labs
  const cartItems = useSelector(
    (state: RootState) => (state as any).productCheckout?.cartItems || [],
  );
  const cartItemCount = cartItems.reduce((sum: number, item: any) => sum + item.quantity, 0);
  const prevCartCount = useRef(cartItemCount);
  const [cartBounce, setCartBounce] = useState(false);
  const isOnProductStorefront = location.pathname.startsWith("/product-storefront");
  const showCartIcon = isOnProductStorefront || cartItemCount > 0;

  useEffect(() => {
    if (cartItemCount > prevCartCount.current) {
      setCartBounce(true);
      const timer = setTimeout(() => setCartBounce(false), 600);
      return () => clearTimeout(timer);
    }
    prevCartCount.current = cartItemCount;
  }, [cartItemCount]);

  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );
  const fullName = userAppSettings
    ? `${userAppSettings.firstName} ${userAppSettings.lastName}`
    : "Your Name";
  const initials = fullName
    .split(" ")
    .map((n) => n[0])
    .join("");
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const checkImpersonation = async () => {
      try {
        const token = await getAccessTokenSilently();
        // Decode JWT payload to check for impersonation claim
        const payload = JSON.parse(atob(token.split(".")[1]));
        const sgiClaims = payload["http://hub.sgi.com/jwt/claims"] ?? {};
        setIsImpersonating(!!sgiClaims["sgi_hub_impersonate_sso_id"]);
      } catch {
        setIsImpersonating(false);
      }
    };
    checkImpersonation();
  }, [getAccessTokenSilently]);

  const handleExitImpersonation = useCallback(async () => {
    await stopCrossAppImpersonation();
    await getAccessTokenSilently({ cacheMode: "off" });
    localStorage.removeItem("selectedCompanyUuid");
    navigate(0);
  }, [navigate, getAccessTokenSilently]);

  const handleHamburgerClick = () => {
    if (isMobile) {
      toggleSidebar();
    } else {
      onSidebarToggle();
    }
  };

  return (
    <div className="sticky top-0 z-10 flex h-14 shrink-0 items-center border-b px-4 sm:gap-x-6 sm:px-6 lg:px-8 bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800/50">
      <button
        className="-m-2.5 p-2.5 transition-colors text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white"
        onClick={handleHamburgerClick}
        type="button"
      >
        <span className="sr-only">Open menu</span>
        <Bars3Icon aria-hidden="true" className="h-6 w-6" />
      </button>
      {/* Search: a wide, centred field-style button that opens the modal */}
      <div className="flex flex-1 justify-center px-3 sm:px-6">
        <button
          className="flex w-full max-w-xl items-center gap-2.5 rounded-lg border px-3.5 py-1.5 text-left transition-colors text-gray-500 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300 dark:text-slate-400 dark:hover:text-white dark:bg-slate-800 dark:hover:bg-slate-700 dark:border-slate-700"
          onClick={() => dispatch(openSearchModal())}
          title="Search (&#8984;K)"
          type="button"
        >
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="h-5 w-5 shrink-0"
          />
          <span className="flex-1 truncate text-sm">
            <span className="sm:hidden">Search</span>
            <span className="hidden sm:inline">
              Search events, resources, news…
            </span>
          </span>
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded border text-gray-500 bg-white border-gray-200 dark:text-slate-500 dark:bg-slate-900 dark:border-slate-700">
            {navigator.platform.includes("Mac") ? "&#8984;" : "Ctrl"}K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-x-4 lg:gap-x-6">
        {/* Cart Button (from labs) */}
        {showCartIcon && (
          <button
            type="button"
            className={clsx(
              "relative p-2 rounded-md transition-all",
              cartItemCount > 0
                ? "text-primary hover:text-primary/80 dark:text-primary dark:hover:text-primary/80 bg-primary/10 hover:bg-primary/15 dark:bg-primary/15 dark:hover:bg-primary/20"
                : "text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300",
              cartBounce && "animate-bounce",
            )}
            onClick={() => navigate("/product-storefront/checkout")}
            title="Shopping Cart"
          >
            <span className="sr-only">Shopping Cart</span>
            <ShoppingCartIcon className={clsx("transition-all", cartItemCount > 0 ? "h-6 w-6" : "h-5 w-5")} aria-hidden="true" />
            {cartItemCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white shadow-sm">
                {cartItemCount > 99 ? "99+" : cartItemCount}
              </span>
            )}
          </button>
        )}

        {/* Theme Toggle */}
        <ThemeToggle />

        <Menu as="div" className="relative">
          <MenuButton className="-m-1.5 flex items-center p-1.5">
            <span className="sr-only">Open user menu</span>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-600">
              <span className="text-sm font-medium leading-none text-white">
                {initials}
              </span>
            </span>
            <span className="hidden lg:flex lg:items-center">
              <span
                aria-hidden="true"
                className="ml-4 text-sm font-semibold leading-6 text-gray-900 dark:text-slate-400"
              >
                {fullName}
              </span>
              <ChevronDownIcon
                aria-hidden="true"
                className="ml-2 h-5 w-5 text-gray-400 dark:text-slate-500"
              />
            </span>
          </MenuButton>
          <MenuItems className="absolute right-0 z-50 mt-2.5 w-32 origin-top-right rounded-md bg-white dark:bg-slate-800 py-2 shadow-lg ring-1 ring-gray-200 dark:ring-slate-700 transition focus:outline-none">
            <MenuItem>
              {({ active }) => (
                <Link
                  className={clsx(
                    active ? "bg-gray-100 dark:bg-slate-700" : "",
                    "block px-3 py-1 text-sm leading-6 text-gray-900 dark:text-slate-100",
                  )}
                  to="/hub/settings"
                >
                  Settings
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={clsx(
                    active ? "bg-gray-100 dark:bg-slate-700" : "",
                    "block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900 dark:text-slate-100",
                  )}
                  onClick={() => navigate("/logout")}
                >
                  Sign out
                </button>
              )}
            </MenuItem>
            {isImpersonating && (
              <>
                <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={clsx(
                        active ? "bg-gray-100 dark:bg-slate-700" : "",
                        "block w-full text-left px-3 py-1 text-sm leading-6 text-red-600 dark:text-red-400",
                      )}
                      onClick={handleExitImpersonation}
                    >
                      Exit Impersonation
                    </button>
                  )}
                </MenuItem>
              </>
            )}
          </MenuItems>
        </Menu>
      </div>
    </div>
  );
};

export default TopNavBar;
